import {
  format,
  parseISO,
  isToday,
  isYesterday,
  startOfWeek,
  endOfWeek,
  addWeeks,
} from "date-fns";
import type { TimeEntry } from "@/types/time";

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export function formatTime12h(iso: string): string {
  return format(parseISO(iso), "h:mm a");
}

export function formatTimeRange(start: string, end: string): string {
  return `${formatTime12h(start)}–${formatTime12h(end)}`;
}

export interface DayGroup {
  date: string;
  label: string;
  fullDate: string;
  totalSeconds: number;
  entries: TimeEntry[];
}

export function groupEntriesByDay(entries: TimeEntry[] | null | undefined): DayGroup[] {
  if (!entries || !Array.isArray(entries)) return [];
  const groups = new Map<string, TimeEntry[]>();

  for (const entry of entries) {
    const d = parseISO(entry.start);
    const date = format(d, "yyyy-MM-dd");
    const existing = groups.get(date) ?? [];
    existing.push(entry);
    groups.set(date, existing);
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, dayEntries]) => {
      const d = parseISO(date);

      let label: string;
      if (isToday(d)) {
        label = "Today";
      } else if (isYesterday(d)) {
        label = "Yesterday";
      } else {
        label = format(d, "EEE, MMM d");
      }

      const fullDate = format(d, "MMMM d, yyyy");
      const totalSeconds = dayEntries.reduce((sum, e) => sum + e.durationSeconds, 0);

      return { date, label, fullDate, totalSeconds, entries: dayEntries };
    });
}

export function getWeekRange(offset = 0): { start: string; end: string } {
  const now = addWeeks(new Date(), offset);
  const monday = startOfWeek(now, { weekStartsOn: 1 });
  const sunday = endOfWeek(now, { weekStartsOn: 1 });

  return {
    start: monday.toISOString(),
    end: sunday.toISOString(),
  };
}

export function formatWeekLabel(start: string, end: string): string {
  return `${format(parseISO(start), "MMM d")} – ${format(parseISO(end), "MMM d")}`;
}

export function toLocalDateString(d: Date): string {
  return format(d, "yyyy-MM-dd");
}
